import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminSidebar from '../components/layout/AdminSidebar';
import {
  DollarSign,
  PieChart,
  TrendingUp,
  TrendingDown,
  Plus,
  FileSpreadsheet,
  Calendar,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Wallet,
  Building,
  Clock,
  Download,
  ChevronRight,
  Loader2,
  Target,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL + '/api/v1';

const STATUS_COLORS = {
  draft: 'bg-gray-500',
  board_review: 'bg-yellow-500',
  approved: 'bg-green-500',
  archived: 'bg-slate-400'
};

const CATEGORY_TYPE_COLORS = {
  operating: 'bg-blue-500',
  reserve: 'bg-purple-500',
  capital: 'bg-amber-500'
};

const BudgetingPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [currentBudget, setCurrentBudget] = useState(null);
  const [reserveItems, setReserveItems] = useState([]);
  const [reserveSummary, setReserveSummary] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showReserveDialog, setShowReserveDialog] = useState(false);
  
  const [newBudget, setNewBudget] = useState({
    fiscal_year: new Date().getFullYear() + 1,
    name: '',
    description: ''
  });
  
  const [newCategory, setNewCategory] = useState({
    name: '',
    category_type: 'operating',
    annual_allocation: 0,
    gl_code: '',
    notes: ''
  });
  
  const [newExpense, setNewExpense] = useState({
    category_id: '',
    amount: 0,
    description: '',
    expense_date: new Date().toISOString().split('T')[0],
    gl_code: ''
  });
  
  const [newReserveItem, setNewReserveItem] = useState({
    asset_name: '',
    description: '',
    estimated_useful_life: 20,
    remaining_useful_life: 10,
    replacement_cost: 0,
    annual_contribution: 0,
    current_funded_amount: 0
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, budgetsRes, reservesRes, summaryRes, forecastRes] = await Promise.all([
        axios.get(`${API}/budgets/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/budgets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/budgets/reserves`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/budgets/reserves/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/budgets/forecast?years=5`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setDashboard(dashboardRes.data);
      setBudgets(budgetsRes.data || []);
      setReserveItems(reservesRes.data || []);
      setReserveSummary(summaryRes.data);
      setForecast(forecastRes.data);
      
      // Load current budget details if available
      if (dashboardRes.data?.budget_id) {
        const budgetRes = await axios.get(`${API}/budgets/${dashboardRes.data.budget_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentBudget(budgetRes.data);
        
        // Load expenses
        const expensesRes = await axios.get(`${API}/budgets/expenses/list?budget_id=${dashboardRes.data.budget_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setExpenses(expensesRes.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBudget = async () => {
    if (!newBudget.name) {
      toast.error('Budget name is required');
      return;
    }

    try {
      const response = await axios.post(`${API}/budgets`, newBudget, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBudgets([response.data.budget, ...budgets]);
      setShowBudgetDialog(false);
      setNewBudget({ fiscal_year: new Date().getFullYear() + 1, name: '', description: '' });
      toast.success('Budget created');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create budget');
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name || !currentBudget) {
      toast.error('Category name is required');
      return;
    }

    try {
      await axios.post(
        `${API}/budgets/${currentBudget.budget_id}/categories`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: newCategory
        }
      );
      setShowCategoryDialog(false);
      setNewCategory({ name: '', category_type: 'operating', annual_allocation: 0, gl_code: '', notes: '' });
      toast.success('Category created');
      fetchData();
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleRecordExpense = async () => {
    if (!newExpense.category_id || !newExpense.amount) {
      toast.error('Category and amount are required');
      return;
    }

    try {
      await axios.post(`${API}/budgets/expenses`, {
        ...newExpense,
        budget_id: currentBudget.budget_id,
        expense_type: 'manual',
        expense_date: new Date(newExpense.expense_date).toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowExpenseDialog(false);
      setNewExpense({ category_id: '', amount: 0, description: '', expense_date: new Date().toISOString().split('T')[0], gl_code: '' });
      toast.success('Expense recorded');
      fetchData();
    } catch (error) {
      toast.error('Failed to record expense');
    }
  };

  const handleCreateReserveItem = async () => {
    if (!newReserveItem.asset_name) {
      toast.error('Asset name is required');
      return;
    }

    try {
      await axios.post(`${API}/budgets/reserves`, newReserveItem, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowReserveDialog(false);
      setNewReserveItem({
        asset_name: '', description: '', estimated_useful_life: 20,
        remaining_useful_life: 10, replacement_cost: 0, annual_contribution: 0, current_funded_amount: 0
      });
      toast.success('Reserve item created');
      fetchData();
    } catch (error) {
      toast.error('Failed to create reserve item');
    }
  };

  const handleExportQuickBooks = async () => {
    try {
      const response = await axios.get(`${API}/budgets/export/quickbooks`, {
        headers: { Authorization: `Bearer ${token}` },
        params: currentBudget ? { budget_id: currentBudget.budget_id } : {}
      });
      
      // Download as CSV
      const blob = new Blob([response.data.content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `budget_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      
      toast.success(`Exported ${response.data.row_count} records`);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString()}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
      </div>
    );
  }

  const pageContent = (
    <div className="p-6 lg:p-8">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-emerald-700 to-emerald-900 text-white px-6 py-6 rounded-xl mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <DollarSign className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Budgeting & Financial Planning</h1>
                <p className="text-emerald-200">Annual Budgets, Reserves & Forecasting</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleExportQuickBooks} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <Download className="w-4 h-4 mr-2" />
                Export QuickBooks
              </Button>
              <Button onClick={() => setShowBudgetDialog(true)} className="bg-white text-emerald-700 hover:bg-emerald-50">
                <Plus className="w-4 h-4 mr-2" />
                New Budget
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Dashboard Overview */}
        {dashboard?.has_budget ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Total Budget</span>
                  <Badge className={STATUS_COLORS[dashboard.status]}>{dashboard.status}</Badge>
                </div>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(dashboard.total_budget)}</p>
                <p className="text-xs text-gray-500">FY {dashboard.fiscal_year}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Actual Spending</span>
                  <TrendingUp className="w-4 h-4 text-gray-400" />
                </div>
                <p className="text-2xl font-bold">{formatCurrency(dashboard.total_actuals)}</p>
                <Progress 
                  value={(dashboard.total_actuals / dashboard.total_budget) * 100} 
                  className="mt-2 h-2"
                />
              </CardContent>
            </Card>
            
            <Card className={dashboard.variance < 0 ? 'border-red-500' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Variance</span>
                  {dashboard.variance >= 0 ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                  )}
                </div>
                <p className={`text-2xl font-bold ${dashboard.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(Math.abs(dashboard.variance))}
                </p>
                <p className="text-xs text-gray-500">
                  {dashboard.variance >= 0 ? 'Under budget' : 'Over budget'} ({dashboard.variance_percent?.toFixed(1)}%)
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Reserve Balance</span>
                  <Shield className="w-4 h-4 text-purple-500" />
                </div>
                <p className="text-2xl font-bold text-purple-600">{formatCurrency(dashboard.reserve_balance)}</p>
                <p className="text-xs text-gray-500">Long-term savings</p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="mb-8 p-8 text-center">
            <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Budget for Current Year</h3>
            <p className="text-gray-600 mb-4">Create a budget for {new Date().getFullYear()} to start tracking finances.</p>
            <Button onClick={() => setShowBudgetDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Budget
            </Button>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="categories">Budget Categories</TabsTrigger>
            <TabsTrigger value="expenses">Expenses</TabsTrigger>
            <TabsTrigger value="reserves">Reserve Study</TabsTrigger>
            <TabsTrigger value="forecast">Forecast</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Budget Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5" />
                    Budget Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.has_budget ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 bg-blue-500 rounded-full" />
                          <span>Operating</span>
                        </div>
                        <span className="font-semibold">{formatCurrency(dashboard.operating_budget)}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 bg-purple-500 rounded-full" />
                          <span>Reserve</span>
                        </div>
                        <span className="font-semibold">{formatCurrency(dashboard.reserve_budget)}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 bg-amber-500 rounded-full" />
                          <span>Capital</span>
                        </div>
                        <span className="font-semibold">{formatCurrency(dashboard.capital_budget)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No budget data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Reserve Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Reserve Study Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {reserveSummary ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-2xl font-bold">{reserveSummary.total_items}</p>
                          <p className="text-sm text-gray-600">Reserve Items</p>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-2xl font-bold text-purple-600">{reserveSummary.funding_percent}%</p>
                          <p className="text-sm text-gray-600">Funded</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Total Funded</span>
                          <span className="font-medium">{formatCurrency(reserveSummary.total_funded)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Replacement Cost</span>
                          <span className="font-medium">{formatCurrency(reserveSummary.total_replacement_cost)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Annual Contribution</span>
                          <span className="font-medium">{formatCurrency(reserveSummary.annual_contribution)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-amber-600">
                          <span>Due in 5 Years</span>
                          <span className="font-medium">{formatCurrency(reserveSummary.upcoming_cost_5yr)}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No reserve data available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Budget Categories</h3>
              {currentBudget && currentBudget.status !== 'approved' && (
                <Button onClick={() => setShowCategoryDialog(true)} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
              )}
            </div>
            
            {currentBudget?.categories?.length > 0 ? (
              <div className="space-y-3">
                {currentBudget.categories.map((cat) => (
                  <Card key={cat.category_id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge className={CATEGORY_TYPE_COLORS[cat.category_type]}>
                            {cat.category_type}
                          </Badge>
                          <div>
                            <h4 className="font-medium">{cat.name}</h4>
                            {cat.gl_code && <p className="text-xs text-gray-500">GL: {cat.gl_code}</p>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{formatCurrency(cat.annual_allocation)}</p>
                          <p className="text-sm text-gray-500">
                            Spent: {formatCurrency(cat.actual || 0)}
                          </p>
                          <p className={`text-xs ${(cat.variance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {(cat.variance || 0) >= 0 ? '+' : ''}{formatCurrency(cat.variance || 0)}
                          </p>
                        </div>
                      </div>
                      <Progress 
                        value={cat.annual_allocation > 0 ? ((cat.actual || 0) / cat.annual_allocation) * 100 : 0}
                        className="mt-2 h-2"
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <p className="text-gray-500">No categories defined. Add categories to track spending.</p>
              </Card>
            )}
          </TabsContent>

          {/* Expenses Tab */}
          <TabsContent value="expenses">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Recorded Expenses</h3>
              {currentBudget && (
                <Button onClick={() => setShowExpenseDialog(true)} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Record Expense
                </Button>
              )}
            </div>
            
            {expenses.length > 0 ? (
              <div className="space-y-3">
                {expenses.map((exp) => (
                  <Card key={exp.expense_id}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">{exp.description}</h4>
                        <p className="text-sm text-gray-500">
                          {new Date(exp.expense_date).toLocaleDateString()} • {exp.expense_type}
                        </p>
                      </div>
                      <p className="text-lg font-semibold">{formatCurrency(exp.amount)}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <p className="text-gray-500">No expenses recorded yet.</p>
              </Card>
            )}
          </TabsContent>

          {/* Reserves Tab */}
          <TabsContent value="reserves">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Reserve Study Items</h3>
              <Button onClick={() => setShowReserveDialog(true)} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Reserve Item
              </Button>
            </div>
            
            {reserveItems.length > 0 ? (
              <div className="space-y-3">
                {reserveItems.map((item) => (
                  <Card key={item.item_id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium">{item.asset_name}</h4>
                          {item.description && <p className="text-sm text-gray-600">{item.description}</p>}
                          <div className="flex gap-4 mt-2 text-sm text-gray-500">
                            <span>Life: {item.remaining_useful_life}/{item.estimated_useful_life} yrs</span>
                            {item.next_replacement && (
                              <span>Replace: {new Date(item.next_replacement).getFullYear()}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{formatCurrency(item.replacement_cost)}</p>
                          <p className="text-sm text-green-600">Funded: {formatCurrency(item.current_funded_amount)}</p>
                          <p className="text-xs text-gray-500">+{formatCurrency(item.annual_contribution)}/yr</p>
                        </div>
                      </div>
                      <Progress 
                        value={item.replacement_cost > 0 ? (item.current_funded_amount / item.replacement_cost) * 100 : 0}
                        className="mt-3 h-2"
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <p className="text-gray-500">No reserve items defined. Add assets to track long-term replacements.</p>
              </Card>
            )}
          </TabsContent>

          {/* Forecast Tab */}
          <TabsContent value="forecast">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  5-Year Financial Forecast
                </CardTitle>
                <CardDescription>
                  Projected expenses and reserve balance based on {forecast?.assumptions?.inflation_rate * 100 || 3}% inflation
                </CardDescription>
              </CardHeader>
              <CardContent>
                {forecast?.forecast?.length > 0 ? (
                  <div className="space-y-4">
                    {forecast.forecast.map((year, i) => (
                      <div key={year.year} className={`p-4 rounded-lg ${i === 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-lg">{year.year}</h4>
                          {i === 0 && <Badge className="bg-emerald-500">Current</Badge>}
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Operating</p>
                            <p className="font-medium">{formatCurrency(year.operating_expenses)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Reserve Contrib.</p>
                            <p className="font-medium">{formatCurrency(year.reserve_contribution)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Total Expenses</p>
                            <p className="font-medium">{formatCurrency(year.total_expenses)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Reserve Balance</p>
                            <p className={`font-medium ${year.reserve_balance < 0 ? 'text-red-600' : 'text-purple-600'}`}>
                              {formatCurrency(year.reserve_balance)}
                            </p>
                          </div>
                        </div>
                        {year.major_replacements?.length > 0 && (
                          <div className="mt-2 p-2 bg-amber-100 rounded text-sm">
                            <span className="font-medium text-amber-800">Major Replacements: </span>
                            {year.major_replacements.join(', ')} ({formatCurrency(year.replacement_cost)})
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No forecast data available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Budget Dialog */}
      <Dialog open={showBudgetDialog} onOpenChange={setShowBudgetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Budget</DialogTitle>
            <DialogDescription>Create an annual budget for financial planning.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Fiscal Year</label>
              <Input
                type="number"
                value={newBudget.fiscal_year}
                onChange={(e) => setNewBudget({ ...newBudget, fiscal_year: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Budget Name</label>
              <Input
                value={newBudget.name}
                onChange={(e) => setNewBudget({ ...newBudget, name: e.target.value })}
                placeholder={`FY ${newBudget.fiscal_year} Annual Budget`}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea
                value={newBudget.description}
                onChange={(e) => setNewBudget({ ...newBudget, description: e.target.value })}
                placeholder="Budget notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBudgetDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateBudget}>Create Budget</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Budget Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Category Name</label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="Landscaping"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Type</label>
              <Select
                value={newCategory.category_type}
                onValueChange={(value) => setNewCategory({ ...newCategory, category_type: value })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operating">Operating</SelectItem>
                  <SelectItem value="reserve">Reserve</SelectItem>
                  <SelectItem value="capital">Capital</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Annual Allocation</label>
              <Input
                type="number"
                value={newCategory.annual_allocation}
                onChange={(e) => setNewCategory({ ...newCategory, annual_allocation: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">GL Code (optional)</label>
              <Input
                value={newCategory.gl_code}
                onChange={(e) => setNewCategory({ ...newCategory, gl_code: e.target.value })}
                placeholder="5100"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCategory}>Add Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Expense Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Category</label>
              <Select
                value={newExpense.category_id}
                onValueChange={(value) => setNewExpense({ ...newExpense, category_id: value })}
              >
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {currentBudget?.categories?.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Amount</label>
              <Input
                type="number"
                value={newExpense.amount}
                onChange={(e) => setNewExpense({ ...newExpense, amount: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Input
                value={newExpense.description}
                onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                placeholder="Invoice #123 - Snow removal"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Date</label>
              <Input
                type="date"
                value={newExpense.expense_date}
                onChange={(e) => setNewExpense({ ...newExpense, expense_date: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpenseDialog(false)}>Cancel</Button>
            <Button onClick={handleRecordExpense}>Record Expense</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Reserve Item Dialog */}
      <Dialog open={showReserveDialog} onOpenChange={setShowReserveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Reserve Item</DialogTitle>
            <DialogDescription>Track long-term assets for replacement planning.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Asset Name</label>
              <Input
                value={newReserveItem.asset_name}
                onChange={(e) => setNewReserveItem({ ...newReserveItem, asset_name: e.target.value })}
                placeholder="Main Road Paving"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea
                value={newReserveItem.description}
                onChange={(e) => setNewReserveItem({ ...newReserveItem, description: e.target.value })}
                placeholder="Details about this asset..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Useful Life (years)</label>
                <Input
                  type="number"
                  value={newReserveItem.estimated_useful_life}
                  onChange={(e) => setNewReserveItem({ ...newReserveItem, estimated_useful_life: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Remaining Life (years)</label>
                <Input
                  type="number"
                  value={newReserveItem.remaining_useful_life}
                  onChange={(e) => setNewReserveItem({ ...newReserveItem, remaining_useful_life: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Replacement Cost</label>
              <Input
                type="number"
                value={newReserveItem.replacement_cost}
                onChange={(e) => setNewReserveItem({ ...newReserveItem, replacement_cost: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Annual Contribution</label>
                <Input
                  type="number"
                  value={newReserveItem.annual_contribution}
                  onChange={(e) => setNewReserveItem({ ...newReserveItem, annual_contribution: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Current Funded</label>
                <Input
                  type="number"
                  value={newReserveItem.current_funded_amount}
                  onChange={(e) => setNewReserveItem({ ...newReserveItem, current_funded_amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReserveDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateReserveItem}>Add Reserve Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
  );

  return <AdminSidebar>{pageContent}</AdminSidebar>;
};

export default BudgetingPage;
